import React, { useEffect, useState } from "react";

const calendar = (
  <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.1667 2.66699H3.83333C3.09695 2.66699 2.5 3.26395 2.5 4.00033V13.3337C2.5 14.07 3.09695 14.667 3.83333 14.667H13.1667C13.903 14.667 14.5 14.07 14.5 13.3337V4.00033C14.5 3.26395 13.903 2.66699 13.1667 2.66699Z" fill="white" stroke="white" strokeWidth="0.888889" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M11.167 1.33301V3.99967" stroke="white" strokeWidth="0.888889" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5.83301 1.33301V3.99967" stroke="black" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M2.5 6.66699H14.5" stroke="black" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const stationIds = {
  '961': 1,
  '907': 2
};

// Helper: Convert "HH:MM" string into total minutes from midnight
const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * 60 + minutes;
};

// Helper: Format 24h string "13:00" to 12h display string "01:00 PM"
const format12Hour = (timeStr) => {
  if (!timeStr) return "";
  let [hours, minutes] = timeStr.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const formattedHours = hours < 10 ? `0${hours}` : hours;
  return `${formattedHours}:${minutes < 10 ? `0${minutes}` : minutes} ${period}`;
};

const PlayerOverlay = ({ station = "907" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scheduleList, setScheduleList] = useState([]);
  const [currentProgram, setCurrentProgram] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDayFinished, setIsDayFinished] = useState(false);

  const [audioInfo, setAudioInfo] = useState(() => JSON.parse(localStorage.getItem("audioInfo") || "{}"));

  useEffect(() => {
    const handleSync = () => {
      const newInfo = localStorage.getItem("audioInfo");
      if (newInfo) setAudioInfo(JSON.parse(newInfo));
    };

    window.addEventListener("storage", handleSync);
    return () => window.removeEventListener("storage", handleSync);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    setIsLoading(true);
    const stationId = stationIds[station] || 1;

    fetch(`/api/v1/radio_contents/${stationId}/`)
      .then((res) => res.json())
      .then((body) => {
        if (!body || !body.data?.today) {
          setScheduleList([]);
          setCurrentProgram(null);
          setIsDayFinished(false);
          return;
        }

        const todayData = body.data.today;
        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();

        // Convert object to array and sort chronologically
        const formattedSchedule = Object.entries(todayData)
          .map(([startTime, program]) => {
            let startMins = timeToMinutes(startTime);
            let endMins = timeToMinutes(program.end_time);

            if (endMins === 0 || endMins < startMins) {
              endMins = 24 * 60;
            }

            let status = "upcoming";
            if (currentMinutes >= startMins && currentMinutes < endMins) {
              status = "live";
            } else if (currentMinutes >= endMins) {
              status = "completed";
            }

            return {
              id: program.program_id,
              startTime,
              endTime: program.end_time,
              startMins,
              timeDisplay: `${startTime} - ${program.end_time}`,
              title: program.program_name,
              host: program.hosts || "Cadena IN",
              cover: program.cover,
              status
            };
          })
          .sort((a, b) => a.startMins - b.startMins);

        const completedList = formattedSchedule.filter((item) => item.status === "completed");
        const liveList = formattedSchedule.filter((item) => item.status === "live");
        const upcomingList = formattedSchedule.filter((item) => item.status === "upcoming");

        const stationName = station === '961' ? 'La 96' : 'La Intensa';

        // Check if all programs for today have ended
        const allFinished = completedList.length === formattedSchedule.length && formattedSchedule.length > 0;
        setIsDayFinished(allFinished);

        if (allFinished) {
          setScheduleList([]);
          const lastProgram = completedList[completedList.length - 1];

          setCurrentProgram({
            title: "Programación Concluida",
            host: "Cadena IN",
            time: `Hasta las ${format12Hour(lastProgram?.endTime || "00:00")}`,
            station: `${stationName}`,
            description: "No hay más programas por el día de hoy. Te invitamos a consultar la programación para los próximos días.",
            badge: "Transmisión Finalizada",
            cover: null
          });
        } else {
          // Normal state: Filter (Last ended + Current live + Next 3 upcoming)
          const lastCompleted = completedList.slice(-1);
          const nextThreeUpcoming = upcomingList.slice(0, 3);
          const filteredList = [...lastCompleted, ...liveList, ...nextThreeUpcoming];

          setScheduleList(filteredList);

          // Hero Details to live program or default to first in list
          const liveShow = liveList[0] || filteredList[0];
          if (liveShow) {
            setCurrentProgram({
              title: liveShow.title,
              host: liveShow.host,
              time: `${format12Hour(liveShow.startTime)} - ${format12Hour(liveShow.endTime)}`,
              station: `${stationName}`,
              description: audioInfo.live ? audioInfo?.liveProgram?.description : `Transmisión en vivo por ${stationName}. Con la conducción de ${liveShow.host}.`,
              badge: liveShow.status === "live" ? "En Vivo Ahora" : "Próximo Programa",
              cover: liveShow.cover
            });
          }
        }
      })
      .catch((err) => {
        console.warn("Error fetching radio schedule:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [isOpen, station]);

  return (
    <>
      {/* Floating trigger button when drawer is collapsed */}
        <button 
          className="overlay-reopen-btn" 
          onClick={() => setIsOpen(!isOpen)}
        >
          {calendar}
          <span>Ver programación</span>
        </button>
      {/* {!isOpen && (
      )} */}

      {/* Bottom Drawer Overlay */}
      <div className={`player-drawer-overlay ${isOpen ? "open" : ""}`}>
        {/* Backdrop overlay */}
        <div className="drawer-backdrop" onClick={() => setIsOpen(false)} />

        {/* Drawer Content */}
        <div className="drawer-content">
          {/* Pull Handle Bar */}
          <div className="drawer-handle-bar" onClick={() => setIsOpen(false)}>
            <div className="handle-pill" />
          </div>

          {/* Current Live Banner */}
          {currentProgram && (
            <section className="current-program-hero">
              <div className="hero-top-bar">
                <div className="live-indicator-tag">
                  <span className="pulse-dot">🔴</span>
                  <span>{currentProgram.badge}</span>
                </div>
                <span className="freq-badge">{currentProgram.station}</span>
              </div>

              <div className="hero-details">
                <span className="time-range">🕒 {currentProgram.time}</span>
                <h1 className="hero-title">{currentProgram.title}</h1>
                {!isDayFinished && (<p className="hero-host">🎙️ Con: <strong>{currentProgram.host}</strong></p>)}
                <p className="hero-description">{currentProgram.description}</p>
              </div>
            </section>
          )}

          {/* Schedule List Area */}
          <section className="schedule-list-container">
            {isLoading ? (
              <div className="loading-state" style={{ textAlign: "center", padding: "20px", color: "#64748b" }}>
                Cargando programación...
              </div>
            ) : isDayFinished ? (
              <div className="day-finished-state" style={{ textAlign: "center", padding: "20px 16px", color: "#64748b" }}>
                <p style={{ margin: "0 0 12px 0", fontSize: "0.95rem", fontWeight: "600" }}>
                  No hay más programas por el día de hoy.
                </p>
                <div className="full-schedule-link-wrapper" style={{ textAlign: "center" }}>
                  <a href={`/parrilla/${audioInfo.id}`} className="full-schedule-link" onClick={() => setIsOpen(false)}>
                    Ver programación completa →
                  </a>
                </div>
              </div>
            ) : scheduleList.length > 0 ? (
              <>
                {scheduleList.map((item) => (
                  <div 
                    key={`${item.id}-${item.startTime}`} 
                    className={`schedule-row ${item.status}`}
                  >
                    <div className="time-col">
                      <span className="time">{item.timeDisplay}</span>
                    </div>

                    <div className="info-col">
                      <div className="info-header">
                        <h3 className="item-title">{item.title}</h3>
                        {item.status === "live" && (
                          <span className="live-tag">EN VIVO</span>
                        )}
                        {item.status === "completed" && (
                          <span className="completed-tag">Finalizado</span>
                        )}
                      </div>
                      <p className="item-host">Con: {item.host}</p>
                    </div>
                  </div>
                ))}

                {/* Link to full schedule page */}
                <div className="full-schedule-link-wrapper" style={{ textAlign: "center", marginTop: "12px" }}>
                  <a href={`/parrilla/${audioInfo.id}`} className="full-schedule-link" onClick={() => setIsOpen(false)}>
                    Ver programación completa →
                  </a>
                </div>
              </>
            ) : (
              <div className="empty-state" style={{ textAlign: "center", padding: "20px", color: "#64748b" }}>
                No hay información de programación disponible para hoy.
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
};

export default PlayerOverlay;